import moment from 'moment';
import { FallbackEpisode, TrackSegmentKind } from '../../../types/fallbackTrackingTypes';
import { toReportRow } from '../../bushistorybybusnumber/csvReports';
import { SEGMENT_STYLES } from './fallbackTrackingStyles';

/** Formats a number of seconds as `[-]m:ss`; empty when the value is not available. */
export function formatSeconds(seconds?: number | null): string {
    if (seconds === null || seconds === undefined)
        return '';
    const sign = seconds < 0 ? '-' : '';
    return sign + moment.utc(Math.abs(seconds) * 1000).format(Math.abs(seconds) >= 3600 ? 'H:mm:ss' : 'm:ss');
}

/** Formats a delay difference in seconds as `+m:ss` / `-m:ss`; empty when the value is not available. */
export function formatSignedSeconds(seconds?: number | null): string {
    if (seconds === null || seconds === undefined)
        return '';
    return seconds > 0 ? `+${formatSeconds(seconds)}` : formatSeconds(seconds);
}

/** A track point as shown by the map slider, with the data of the segment it belongs to. */
export interface TrackPointView {
    kind: TrackSegmentKind;
    time: string;
    latitude: Latitude;
    longitude: Longitude;
    delaySec?: number | null;
    /** Only set on the last point of a recovered synthetic segment. */
    recoveryOffsetMeters?: number | null;
}

/** Popup text of a track point: origin, time, delay (kept delay when synthetic) and the fallback error at recovery. */
export function describeTrackPoint(point: TrackPointView): string {
    const parts = [`${SEGMENT_STYLES[point.kind].label} at ${moment.parseZone(point.time).format('h:mm:ss A')}`];
    if (point.delaySec !== null && point.delaySec !== undefined)
        parts.push(`${point.kind === 'Synthetic' ? 'kept delay' : 'delay'} ${formatSeconds(point.delaySec)}`);
    if (point.recoveryOffsetMeters !== null && point.recoveryOffsetMeters !== undefined)
        parts.push(`ended ${Math.abs(point.recoveryOffsetMeters)} m ${point.recoveryOffsetMeters >= 0 ? 'ahead of' : 'behind'} the vehicle`);
    return parts.join(' · ');
}

const formatDateTime = (time: string) => moment.parseZone(time).format('YYYY-MM-DD hh:mm:ss A');

const FallbackEpisodesReportDef = [
    { header: 'Route', getter: (e: FallbackEpisode) => e.routeShortName },
    { header: 'Vehicle', getter: (e: FallbackEpisode) => e.vehicleId },
    { header: 'Trip', getter: (e: FallbackEpisode) => e.tripId },
    { header: 'Last Position', getter: (e: FallbackEpisode) => formatDateTime(e.start) },
    { header: 'Next Position', getter: (e: FallbackEpisode) => formatDateTime(e.end) },
    { header: 'Fallback End', getter: (e: FallbackEpisode) => formatDateTime(e.fallbackEnd) },
    { header: 'Gap (sec)', getter: (e: FallbackEpisode) => e.durationSec },
    { header: 'Kept Delay (sec)', getter: (e: FallbackEpisode) => e.observedDelaySec },
    { header: 'Delay At Recovery (sec)', getter: (e: FallbackEpisode) => e.recoveryDelaySec },
    { header: 'Delay Error (sec)', getter: (e: FallbackEpisode) => e.delayErrorSec },
    { header: 'Outcome', getter: (e: FallbackEpisode) => e.outcome },
];

export const FallbackEpisodesReportHeaders = FallbackEpisodesReportDef.map(column => column.header);

export const toFallbackEpisodesReport = (episodes: FallbackEpisode[]): object[] =>
    episodes.map(e => toReportRow(FallbackEpisodesReportDef, e));
